import type { OptionPageData, SystemDiagramKey } from '@core/presentationPrint';
import { formatCostPerYear, BEHAVIORAL_ARCHETYPES } from '@core/presentationPrint';
import { ComfortChart } from './ComfortChart';
import { ControlPrecisionSection } from '../visualizer/ControlPrecisionSection';

interface OptionPageProps {
  data: OptionPageData;
}

/**
 * Inline SVG icons for boilers and cylinders
 * These are baked into the component to ensure reliable PDF/print rendering
 */
const Icons = {
  Boiler: () => (
    <svg width="40" height="60" viewBox="0 0 40 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="icon-svg" aria-hidden="true">
      <rect width="40" height="60" rx="4" fill="#E2E8F0" />
      <rect x="5" y="45" width="30" height="10" rx="2" fill="#94A3B8" />
      <circle cx="20" cy="15" r="5" stroke="#475569" strokeWidth="2" fill="none" />
      <rect x="15" y="25" width="10" height="15" rx="1" fill="#64748B" />
    </svg>
  ),
  Cylinder: () => (
    <svg width="40" height="70" viewBox="0 0 40 70" fill="none" xmlns="http://www.w3.org/2000/svg" className="icon-svg" aria-hidden="true">
      <path d="M0 10C0 4.47715 4.47715 0 10 0H30C35.5228 0 40 4.47715 40 10V60C40 65.5228 35.5228 70 30 70H10C4.47715 70 0 65.5228 0 60V10Z" fill="#CBD5E1" />
      <rect x="0" y="20" width="40" height="2" fill="#94A3B8" />
      <rect x="0" y="50" width="40" height="2" fill="#94A3B8" />
      <circle cx="20" cy="35" r="8" fill="#E0E7EF" />
    </svg>
  ),
  HeatPump: () => (
    <svg width="40" height="60" viewBox="0 0 40 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="icon-svg" aria-hidden="true">
      <rect width="40" height="60" rx="4" fill="#D1FAE5" />
      <path d="M12 20 L20 30 L12 40" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M20 20 L28 30 L20 40" stroke="#059669" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <rect x="8" y="48" width="24" height="8" rx="2" fill="#059669" />
    </svg>
  ),
  // Small F&E tank (Feed & Expansion)
  TankFE: () => (
    <svg width="30" height="40" viewBox="0 0 30 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="icon-svg" aria-hidden="true">
      <rect x="3" y="10" width="24" height="26" rx="2" fill="#DBEAFE" stroke="#3B82F6" strokeWidth="1.5" />
      <ellipse cx="15" cy="10" rx="12" ry="4" fill="#BFDBFE" />
      <text x="15" y="27" textAnchor="middle" fontSize="7" fill="#1e40af" fontWeight="600">F&E</text>
    </svg>
  ),
  // Large CWS tank (Cold Water Storage)
  TankCWS: () => (
    <svg width="50" height="60" viewBox="0 0 50 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="icon-svg" aria-hidden="true">
      <rect x="5" y="12" width="40" height="42" rx="3" fill="#DBEAFE" stroke="#3B82F6" strokeWidth="2" />
      <ellipse cx="25" cy="12" rx="20" ry="6" fill="#BFDBFE" />
      <rect x="12" y="22" width="26" height="2" fill="#60A5FA" />
      <rect x="12" y="32" width="26" height="2" fill="#60A5FA" />
      <rect x="12" y="42" width="26" height="2" fill="#60A5FA" />
      <text x="25" y="56" textAnchor="middle" fontSize="8" fill="#1e40af" fontWeight="600">CWS</text>
    </svg>
  ),
  // Generic tank (fallback)
  Tank: () => (
    <svg width="40" height="60" viewBox="0 0 40 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="icon-svg" aria-hidden="true">
      <rect x="5" y="15" width="30" height="40" rx="3" fill="#DBEAFE" stroke="#3B82F6" strokeWidth="2" />
      <ellipse cx="20" cy="15" rx="15" ry="6" fill="#BFDBFE" />
      <rect x="10" y="25" width="20" height="2" fill="#60A5FA" />
      <rect x="10" y="35" width="20" height="2" fill="#60A5FA" />
      <rect x="10" y="45" width="20" height="2" fill="#60A5FA" />
    </svg>
  )
};

/**
 * Tank configuration types for clear distinction
 */
type TankConfig = 'two_tanks' | 'fe_only' | 'none';

/**
 * Get tank configuration label for display
 */
function getTankConfigLabel(tankConfig: TankConfig): string {
  switch (tankConfig) {
    case 'two_tanks':
      return 'Loft tanks: F&E + CWS';
    case 'fe_only':
      return 'Loft tanks: F&E only';
    case 'none':
      return 'No loft tanks (mains-fed)';
  }
}

/**
 * Simple inline SVG system diagram for print mode
 * Uses inline SVG icons that render reliably in print/PDF
 */
function SystemLayoutDiagram({ systemType }: { systemType: SystemDiagramKey }) {
  const descriptions: Record<SystemDiagramKey, {
    title: string;
    components: Array<{ icon: keyof typeof Icons; label: string; size?: string }>;
    tankConfig: TankConfig;
  }> = {
    combi: {
      title: 'Combi Boiler System',
      components: [
        { icon: 'Boiler', label: 'Wall-mounted combi boiler (heats water on demand)' }
      ],
      tankConfig: 'none'
    },
    system_cylinder: {
      title: 'System Boiler + Unvented Cylinder',
      components: [
        { icon: 'Boiler', label: 'System boiler' },
        { icon: 'Cylinder', label: 'Unvented cylinder' }
      ],
      tankConfig: 'none'
    },
    regular_vented: {
      title: 'Regular Boiler + Vented System',
      components: [
        { icon: 'Boiler', label: 'Regular boiler' },
        { icon: 'Cylinder', label: 'Hot water cylinder' },
        { icon: 'TankFE', label: 'F&E tank (~25L)', size: 'small' },
        { icon: 'TankCWS', label: 'CWS tank (~227L)', size: 'large' }
      ],
      tankConfig: 'two_tanks'
    },
    regular_unvented: {
      title: 'Regular Boiler + Unvented Cylinder (Sealed)',
      components: [
        { icon: 'Boiler', label: 'Regular boiler' },
        { icon: 'Cylinder', label: 'Unvented cylinder' },
        { icon: 'TankFE', label: 'F&E tank (~15L)', size: 'small' }
      ],
      tankConfig: 'fe_only'
    },
    heat_pump: {
      title: 'Heat Pump System',
      components: [
        { icon: 'HeatPump', label: 'Heat pump unit (external)' },
        { icon: 'Cylinder', label: 'Hot water cylinder' }
      ],
      tankConfig: 'none'
    },
    unknown: {
      title: 'Heating System',
      components: [
        { icon: 'Boiler', label: 'Heat source' }
      ],
      tankConfig: 'none'
    }
  };

  const config = descriptions[systemType] || descriptions.unknown;

  return (
    <div style={{
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      padding: '16px',
      background: '#f8fafc',
      maxWidth: '500px',
      margin: '0 auto'
    }}>
      <div style={{
        fontSize: '14px',
        fontWeight: 600,
        color: '#1e293b',
        marginBottom: '12px',
        textAlign: 'center'
      }}>
        {config.title}
      </div>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        justifyContent: 'center',
        alignItems: 'flex-end'
      }}>
        {config.components.map((component, idx) => (
          <div key={idx} style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            minWidth: component.size === 'large' ? '90px' : component.size === 'small' ? '60px' : '80px'
          }}>
            <div className="icon-wrapper" style={{
              padding: '8px',
              background: 'white',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {Icons[component.icon]()}
            </div>
            <div style={{
              fontSize: '10px',
              color: '#475569',
              textAlign: 'center',
              fontWeight: 500,
              lineHeight: 1.3
            }}>
              {component.label}
            </div>
          </div>
        ))}
      </div>
      {/* Tank configuration summary line */}
      <div style={{
        marginTop: '12px',
        paddingTop: '12px',
        borderTop: '1px solid #e2e8f0',
        fontSize: '11px',
        color: '#64748b',
        textAlign: 'center',
        fontWeight: 500
      }}>
        {getTankConfigLabel(config.tankConfig)}
      </div>
    </div>
  );
}

/**
 * Render dots string as styled spans with proper CSS classes
 * Maps emoji dots to CSS-styled elements for reliable print/PDF rendering
 */
function renderDots(dotsString: string, verdict?: string): React.ReactNode {
  // Map each character to a styled dot
  const dots = dotsString.split('').map((char, idx) => {
    let dotClass = 'dot-neutral'; // default

    if (char === '🟢') {
      dotClass = 'dot-green';
    } else if (char === '🔴') {
      dotClass = 'dot-red';
    } else if (char === '🟠') {
      dotClass = 'dot-orange';
    } else if (char === '⚪') {
      dotClass = 'dot-neutral';
    } else {
      // For non-emoji characters (like ●), infer from verdict
      if (verdict === 'supports') {
        dotClass = 'dot-green';
      } else if (verdict === 'conflicts') {
        dotClass = 'dot-red';
      } else if (verdict === 'partial') {
        dotClass = 'dot-orange';
      }
    }

    return (
      <span key={idx} className={`dp-dot ${dotClass}`}>●</span>
    );
  });

  return <span className="dp-dots-container">{dots}</span>;
}

/**
 * Format factor name for display (title case)
 */
function formatFactorName(factor: string): string {
  const factorNames: Record<string, string> = {
    'multiple_taps': 'Multiple Taps',
    'heating_during_high_hot_water_demand': 'Heating During Hot Water',
    'airing_clothes': 'Airing Clothes',
    'space': 'Space',
    'reliability': 'Reliability',
    'longevity': 'Longevity',
    'efficiency': 'Efficiency',
    'future_compatibility': 'Future Compatibility',
    'being_green': 'Being Green',
    'noise': 'Noise',
  };
  return factorNames[factor] || factor.replace(/_/g, ' ');
}

/**
 * One A4 page per selected option.
 * Sections A–F as specified in the Decision Pack v2.
 */
export function OptionPage({ data }: OptionPageProps) {
  const { header, systemDiagramKey, comfort, worksBestWhen, suitability, annualCost, planningNote, experienceFit, controlPrecision, pipeBoreNote } = data;

  return (
    <div className="dp-page dp-option-page">
      {/* Section A — Option header */}
      <section className="dp-section-a">
        <h2 className="dp-system-name">{header.systemName}</h2>
        <p className="dp-sub-line">{header.subLine}</p>
      </section>

      {/* System Layout diagram (always present) */}
      <section className="dp-system-diagram">
        <h3 className="dp-system-layout-title">System Layout</h3>
        <SystemLayoutDiagram systemType={systemDiagramKey} />
      </section>

      {/* Section B — Comfort behaviour (archetype-led, de-duplicated) */}
      <section className="dp-section-b">
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '8px',
          padding: '8px 12px',
          background: '#f0f9ff',
          borderLeft: '3px solid #2563eb',
          borderRadius: '0 4px 4px 0'
        }}>
          <span style={{ fontSize: '20px', lineHeight: 1 }}>
            {BEHAVIORAL_ARCHETYPES[comfort.archetype].icon}
          </span>
          <div>
            <div style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>
              {BEHAVIORAL_ARCHETYPES[comfort.archetype].label}
            </div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>
              {BEHAVIORAL_ARCHETYPES[comfort.archetype].summary}
            </div>
          </div>
        </div>
        <ComfortChart title={comfort.chartTitle} curve={comfort.curve} />
        <ul className="dp-comfort-bullets">
          {comfort.bullets.map((bullet, i) => (
            <li key={i}>{bullet}</li>
          ))}
        </ul>
      </section>

      {/* Section C — Works best when (occupancy-aware) */}
      <section className="dp-section-c">
        <h3>Works best when</h3>
        <p className="dp-works-best-bullet">{worksBestWhen.bullet}</p>
      </section>

      {/* Section D — Suitability for this home (hot water + emitters + retrofit) */}
      <section className="dp-section-d">
        <h3>Suitability for this home</h3>
        
        {/* Hot Water Capability - simplified with reference to table */}
        <div className="dp-hot-water-block">
          <h4>Hot Water Capability</h4>
          <div className="dp-hot-water-content">
            <p><strong>{suitability.hotWater.summary}</strong></p>
            <p style={{ fontSize: '11px', fontStyle: 'italic', color: '#64748b', marginTop: '4px' }}>
              {suitability.hotWater.referenceText}
            </p>
          </div>
        </div>
        
        {/* Property characteristics */}
        <div className="dp-suitability-columns">
          <div className="dp-suitability-col">
            <h4>What helps this option</h4>
            <ul>
              <li>Insulation level: {suitability.whatHelps.insulationLevel}</li>
              <li>Emitters: {suitability.whatHelps.emitters}</li>
              <li>Low-temp readiness confidence: {suitability.whatHelps.lowTempReadiness}</li>
            </ul>
          </div>
          {suitability.thingsToBeAware.length > 0 && (
            <div className="dp-suitability-col">
              <h4>Things to be aware of</h4>
              <ul>
                {suitability.thingsToBeAware.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {/* Experience Fit (if available) */}
      {experienceFit && experienceFit.length > 0 && (
        <section className="dp-section-experience-fit">
          <h3>How this matches your priorities</h3>
          <div className="dp-experience-fit-list">
            {experienceFit.map((fit, i) => (
              <div key={i} className="dp-experience-fit-item">
                <div className="dp-experience-fit-header">
                  <span className="dp-experience-fit-dots">
                    {renderDots(fit.dots, fit.verdict)}
                  </span>
                  <span className="dp-experience-fit-factor">
                    {formatFactorName(fit.factor)}
                  </span>
                </div>
                {fit.isConflict && fit.conflictNote && (
                  <p className="dp-experience-fit-conflict">
                    <span className={`dp-dot ${fit.verdict === 'conflicts' ? 'dot-red' : 'dot-orange'}`}>●</span> {fit.conflictNote}
                  </p>
                )}
                {fit.comment && (
                  <p className="dp-experience-fit-comment">{fit.comment}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Control Precision Section (for gas boiler options) */}
      {controlPrecision?.showSection && (
        <section className="dp-section-control-precision">
          <ControlPrecisionSection
            boilerSupportsLoadCompensation={controlPrecision.boilerSupportsLoadCompensation}
            printMode={true}
          />
        </section>
      )}

      {/* Pipe Bore Note (for heat pump options with microbore) */}
      {pipeBoreNote && (
        <section className="dp-section-pipe-note">
          <div className="dp-pipe-note-box">
            <h4>Pipework Consideration</h4>
            <p>{pipeBoreNote}</p>
          </div>
        </section>
      )}

      {/* Section E — Energy & Running Cost (improved presentation) */}
      <section className="dp-section-e">
        <h3>Energy & Running Cost</h3>
        <p className="dp-energy-explainer">
          Both systems deliver the same heat. The difference is how much fuel is required to produce it.
        </p>
        <div className="dp-cost-block">
          {/* Delivered heat row - same for both systems */}
          <div className="dp-cost-row dp-cost-row-same">
            <span className="dp-cost-label">
              <span className="dp-badge dp-badge-same">=</span>
              Delivered heat demand:
            </span>
            <span className="dp-cost-value dp-cost-value-muted">
              Same
            </span>
          </div>

          {/* Fuel input required / running cost - where the difference is */}
          <div className="dp-cost-row">
            <span className="dp-cost-label">Fuel input required (current):</span>
            <span className="dp-cost-value">
              {formatCostPerYear(annualCost.currentSystemCostPerYear)}
            </span>
          </div>
          <div className="dp-cost-row dp-cost-row-highlight dp-cost-row-savings">
            <span className="dp-cost-label">
              {annualCost.thisOptionCostPerYear !== null &&
               annualCost.currentSystemCostPerYear !== null &&
               annualCost.thisOptionCostPerYear < annualCost.currentSystemCostPerYear && (
                <span className="dp-badge dp-badge-saving">↓</span>
              )}
              Fuel input required (this option):
            </span>
            <span className="dp-cost-value">
              {formatCostPerYear(annualCost.thisOptionCostPerYear)}
            </span>
          </div>

          {/* Show savings if applicable */}
          {annualCost.thisOptionCostPerYear !== null &&
           annualCost.currentSystemCostPerYear !== null && (
            <div className="dp-cost-row dp-cost-row-delta">
              <span className="dp-cost-label">Annual saving:</span>
              <span className={`dp-cost-value ${
                annualCost.thisOptionCostPerYear < annualCost.currentSystemCostPerYear
                  ? 'dp-cost-saving'
                  : 'dp-cost-increase'
              }`}>
                {annualCost.thisOptionCostPerYear < annualCost.currentSystemCostPerYear ? '↓ Save ' : '↑ '}
                £{Math.abs(Math.round(annualCost.currentSystemCostPerYear - annualCost.thisOptionCostPerYear)).toLocaleString()}/year
              </span>
            </div>
          )}

          {/* System efficiency comparison */}
          <div className="dp-cost-row dp-cost-row-efficiency">
            <span className="dp-cost-label">System efficiency (implied):</span>
            <span className="dp-cost-value">
              <span style={{ color: '#94a3b8' }}>Lower</span>
              {' → '}
              <span style={{ color: '#16a34a' }}>Higher</span>
            </span>
          </div>
        </div>
        <p className="dp-cost-footnote">{annualCost.footnote}</p>
      </section>

      {/* Quoted Install Price (BG style - separate from savings) */}
      {data.quotedInstallPriceGBP !== undefined && (
        <section className="dp-section-quote">
          <div className="dp-quote-box">
            <h3>{data.quoteLabel || 'Quoted installed price'}</h3>
            <div className="dp-quote-value">
              £{data.quotedInstallPriceGBP.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </div>
            <p className="dp-quote-disclaimer">
              Quote shown separately from running-cost estimates.
            </p>
          </div>
        </section>
      )}

      {/* Section F — Planning note */}
      <section className="dp-section-f">
        <p className="dp-planning-note">{planningNote}</p>
      </section>
    </div>
  );
}
